import { useEffect, useReducer, useState } from 'react';
import projectsOverviewStore, {
  INITIAL_PROJECTS_OVERVIEW,
} from '../store/projectsOverview/projectsOverviewStore';
import {
  ProjectsOverviewStoreKind,
  ProjectsOverviewStoreType,
} from 'src/store/projectsOverview/projectsOverviewStore.types';
import { getProjectsOverview } from 'src/services/getProjectsOverview';
import { FiltersStoreType } from 'src/store/filters/filtersStore.types';
import { Language } from 'src/store/language/languageStore.types';
import { ReportYears } from 'src/store/baseUrl/baseUrlStore.types';
import projectRequestCountStore, {
  INITIAL_PROJECT_REQUEST_COUNT,
} from 'src/store/projectRequestCount/projectRequestCountStore';
import {
  ProjectRequestCountStoreKind,
  ProjectRequestCountStoreType,
} from 'src/store/projectRequestCount/projectRequestCountStore.types';

export interface ProjectsOverviewHook {
  state: ProjectsOverviewStoreType;
  actions: {
    getProjects: (
      props: FiltersStoreType,
      language: Language,
      reportYears: ReportYears
    ) => void;
  };
  prcState: ProjectRequestCountStoreType;
}

const useProjectsOverviewHook = (): ProjectsOverviewHook => {
  const [state, dispatch] = useReducer(
    projectsOverviewStore,
    INITIAL_PROJECTS_OVERVIEW
  );

  const [requestQueue] = useState<
    Array<{
      props: FiltersStoreType;
      language: Language;
      reportYears: ReportYears;
    }>
  >([]);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveCount, setResolveCount] = useState(0);
  const [prcState, prcDispatch] = useReducer(
    projectRequestCountStore,
    INITIAL_PROJECT_REQUEST_COUNT
  );

  const resolvePromise: () => void = () => {
    // Only resolve the most recent promise
    const tempPromiseLength = requestQueue.length;
    // When the detail page is open the tempPromiseLength
    // is zero and without a check the code would still
    // try to resolve a promise.
    if (tempPromiseLength > 0) {
      const args = requestQueue[requestQueue.length - 1];
      getProjectsOverview(args.props, args.language, args.reportYears)
        .then(value => {
          // Only write it in the store when it is
          // still the most recent promise
          if (tempPromiseLength === requestQueue.length) {
            dispatch({
              type: ProjectsOverviewStoreKind.INITIAL,
              payload: value,
            });
            setIsResolving(false);
            prcDispatch({
              type: ProjectRequestCountStoreKind.INITIAL,
            });
          } else {
            resolvePromise();
          }
        })
        .catch(() => {
          // throw new Error('Unable to resolve projects');
        });
    } else {
      setIsResolving(false);
    }
  };

  useEffect(() => {
    if (!isResolving) {
      setIsResolving(true);
      resolvePromise();
    }
  }, [resolveCount]);

  const getProjects: (
    props: FiltersStoreType,
    language: Language,
    reportYears: ReportYears
  ) => void = (props, language, reportYears) => {
    requestQueue.push({ props, language, reportYears });
    setResolveCount(resolveCount + 1);
  };
  return { state, actions: { getProjects }, prcState };
};

export default useProjectsOverviewHook;
